import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Printer } from 'lucide-react';
import { useOperacaoDetalhe } from '@/modules/credito/operacoes-api';
import { formatDateBR, formatBRL, formatCPF, formatName } from '@/lib/format';
import type { Operacao } from '@/modules/credito/operacoes-schemas';

export const Route = createFileRoute('/_print/relatorio-operacao/$opId')({
  component: RelatorioOperacaoRoute,
});

const PRINT_CSS = `
  :root {
    --font-sans: -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Arial, sans-serif;
    --font-mono: "SF Mono", ui-monospace, "JetBrains Mono", Menlo, Consolas, monospace;
    --ink:   #1a1a1a;
    --ink-2: #4a4a4a;
    --ink-3: #7a7a7a;
    --ink-4: #a8a8a8;
    --paper: #ffffff;
    --card:  #f3f3f2;
    --rule:  #e5e7eb;
    --rule-2:#ececea;
    --brand: #d97757;
    --ok:    #22c55e;
    --alert: #f59e0b;
    --irr:   #ef4444;
    --page-my: 16mm;
    --page-mx: 20mm;
  }
  html, body {
    background: #e8e8e6 !important;
    font-family: var(--font-sans);
    color: var(--ink) !important;
    margin: 0;
    -webkit-font-smoothing: antialiased;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .rel-shell * { box-sizing: border-box; }
  .rel-shell p, .rel-shell h1, .rel-shell h2, .rel-shell h3 { margin: 0; padding: 0; }

  /* Toolbar */
  .rel-toolbar {
    position: sticky; top: 0; z-index: 10;
    background: rgba(255,255,255,0.95);
    backdrop-filter: saturate(180%) blur(10px);
    border-bottom: 0.75pt solid var(--rule);
    padding: 10px 20px;
    display: flex; justify-content: space-between; align-items: center; gap: 12px;
    font-size: 12px;
    color: var(--ink-3);
  }
  .rel-toolbar strong { color: var(--ink-2); font-weight: 600; }
  .rel-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 7px 14px;
    border-radius: 6px;
    border: none;
    background: var(--brand);
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: .02em;
    cursor: pointer;
    transition: opacity 0.15s ease;
  }
  .rel-btn:hover { opacity: 0.9; }

  /* Wrapper */
  .doc { padding: 32px 0 64px; display: flex; flex-direction: column; align-items: center; gap: 20px; }

  /* A4 Page */
  .page {
    width: 210mm; min-height: 297mm;
    background: var(--paper);
    padding: var(--page-my) var(--page-mx);
    box-shadow: 0 1px 2px rgba(0,0,0,.04), 0 8px 28px rgba(0,0,0,.07);
    display: flex; flex-direction: column; position: relative;
  }
  .page::before {
    content: attr(data-pn); position: absolute; top: -20px; left: 6px;
    font-family: var(--font-mono); font-size: 10px; color: var(--ink-4); letter-spacing: .08em;
  }

  /* Running header */
  .rh {
    display: flex; justify-content: space-between; align-items: center;
    padding-bottom: 6mm; border-bottom: 0.75pt solid var(--rule);
    font-size: 8pt; color: var(--ink-4); letter-spacing: .02em;
  }
  .rh {
    padding-bottom: 4mm;
  }
  .rh .brand { display: flex; align-items: center; }
  .rh .brand img { height: 6mm; width: auto; display: block; }
  .rh .meta { font-family: var(--font-mono); font-size: 8pt; }

  /* Doc header */
  .doc-label { margin-top: 7mm; font-size: 10pt; font-weight: 600; color: var(--brand); letter-spacing: .14em; text-transform: uppercase; }
  .doc-title { margin-top: 2.5mm; font-size: 19pt; font-weight: 600; color: var(--ink); letter-spacing: -0.01em; line-height: 1.15; }
  .doc-sub { margin-top: 2mm; font-size: 10.5pt; color: var(--ink-3); }

  /* Client plate */
  .client-plate { margin-top: 7mm; display: grid; grid-template-columns: 1fr 1.6fr 1fr; border: 0.75pt solid var(--rule); }
  .cp-cell { padding: 4mm 5mm; border-right: 0.75pt solid var(--rule); display: flex; flex-direction: column; gap: 1.5mm; }
  .cp-cell:last-child { border-right: none; }
  .cp-cell .k { font-size: 7.5pt; font-weight: 600; color: var(--ink-3); letter-spacing: .1em; text-transform: uppercase; }
  .cp-cell .v { font-size: 10pt; color: var(--ink); font-weight: 500; }
  .cp-cell .v.mono { font-family: var(--font-mono); font-size: 9.5pt; }

  /* Metric strip */
  .metric-strip { margin-top: 5mm; display: grid; grid-template-columns: repeat(4, 1fr); gap: 3mm; }
  .metric-card { background: var(--card); padding: 4mm 5mm; border-radius: 2px; display: flex; flex-direction: column; gap: 1.5mm; }
  .metric-card.accent { background: color-mix(in oklab, var(--brand) 8%, white); }
  .metric-card .m-label { font-size: 7.5pt; font-weight: 600; color: var(--ink-3); letter-spacing: .1em; text-transform: uppercase; }
  .metric-card.accent .m-label { color: color-mix(in oklab, var(--brand) 65%, #333); }
  .metric-card .m-value { font-size: 12.5pt; font-weight: 600; color: var(--ink); letter-spacing: -0.02em; line-height: 1.1; }
  .metric-card.accent .m-value { color: var(--brand); }
  .metric-card .m-sub { font-size: 7.5pt; color: var(--ink-4); font-family: var(--font-mono); }

  /* Section */
  .section { margin-top: 6mm; }
  .section-head {
    display: flex; align-items: baseline; justify-content: space-between;
    padding-bottom: 2.5mm; border-bottom: 0.75pt solid var(--rule);
  }
  .s-num { font-family: var(--font-mono); font-size: 8.5pt; color: var(--ink-4); margin-right: 7px; }
  .s-title { font-size: 10pt; font-weight: 600; color: var(--brand); letter-spacing: .12em; text-transform: uppercase; flex: 1; }
  .s-meta { font-family: var(--font-mono); font-size: 8pt; color: var(--ink-4); }

  /* Op grid (2-col key/value) */
  .op-grid { display: grid; grid-template-columns: 1fr 1fr; margin-top: 3mm; border: 0.75pt solid var(--rule); }
  .op-cell { display: flex; flex-direction: column; gap: 1mm; padding: 2.5mm 4mm; border-bottom: 0.5pt solid var(--rule-2); border-right: 0.5pt solid var(--rule-2); }
  .op-cell:nth-child(even) { border-right: none; }
  .op-cell:nth-last-child(-n+2) { border-bottom: none; }
  .op-cell .ck { font-size: 7.5pt; color: var(--ink-4); font-weight: 500; letter-spacing: .06em; text-transform: uppercase; }
  .op-cell .cv { font-size: 9.5pt; color: var(--ink); font-weight: 500; }
  .op-cell .cv.dash { color: var(--ink-4); }

  /* Data table */
  .data-table { width: 100%; border-collapse: collapse; margin-top: 2mm; }
  .data-table th {
    font-size: 7.5pt; font-weight: 600; color: var(--ink-3);
    letter-spacing: .08em; text-transform: uppercase;
    padding: 2mm 3mm 2mm 0; text-align: left; border-bottom: 0.75pt solid var(--rule);
  }
  .data-table th.r, .data-table td.r { text-align: right; padding-right: 0; }
  .data-table td {
    padding: 2.5mm 3mm 2.5mm 0; font-size: 9pt; color: var(--ink-2);
    border-bottom: 0.5pt solid var(--rule-2); vertical-align: middle;
  }
  .data-table tr:last-child td { border-bottom: none; }
  .data-table td.strong { color: var(--ink); font-weight: 600; }
  .data-table td.mono { font-family: var(--font-mono); font-size: 8.5pt; }
  .data-table td.dash { color: var(--ink-4); }
  .data-table .sub { display: block; font-size: 7.5pt; color: var(--ink-4); margin-top: 1mm; }

  /* Progress row */
  .progress-row { display: flex; align-items: center; gap: 5mm; margin: 4mm 0 2mm; }
  .progress-segs { display: flex; gap: 2mm; flex: 1; }
  .seg { height: 4px; flex: 1; border-radius: 2px; background: var(--rule); }
  .seg.paid { background: var(--ok); }
  .seg.late { background: var(--irr); }
  .seg.pend { background: var(--rule); }
  .progress-label { font-family: var(--font-mono); font-size: 8pt; color: var(--ink-3); white-space: nowrap; }
  .progress-label b { color: var(--ink); }

  /* Badge */
  .badge {
    display: inline-block;
    font-size: 7.5pt; font-weight: 600; font-family: var(--font-mono);
    letter-spacing: .06em; padding: 1.5px 7px; border-radius: 999px; white-space: nowrap;
  }
  .badge.ok { background: color-mix(in oklab, var(--ok) 12%, white); color: #16a34a; }
  .badge.pend { background: var(--card); color: var(--ink-4); }
  .badge.irr { background: color-mix(in oklab, var(--irr) 12%, white); color: #b91c1c; }

  /* Deposit block */
  .deposit-block {
    margin-top: 3mm;
    border: 0.75pt solid var(--rule); border-radius: 2px;
    display: grid; grid-template-columns: 1fr 1fr;
  }
  .db-cell { padding: 3.5mm 5mm; border-right: 0.5pt solid var(--rule-2); border-bottom: 0.5pt solid var(--rule-2); display: flex; flex-direction: column; gap: 1mm; }
  .db-cell:nth-child(even) { border-right: none; }
  .db-cell:nth-last-child(-n+2) { border-bottom: none; }
  .db-cell .dk { font-size: 7.5pt; color: var(--ink-4); letter-spacing: .06em; text-transform: uppercase; font-weight: 500; }
  .db-cell .dv { font-size: 9pt; color: var(--ink); font-weight: 500; }
  .db-cell .dv.small { font-size: 8pt; color: var(--ink-2); }

  /* Pendências callout */
  .pend-callout {
    margin-top: 3mm; padding: 4mm 5mm;
    border-left: 2pt solid var(--alert);
    background: color-mix(in oklab, var(--alert) 6%, white);
  }
  .pend-callout ul { margin: 0; padding-left: 5mm; font-size: 9pt; color: var(--ink-2); }
  .pend-callout li { margin-bottom: 1mm; }
  .pend-callout li:last-child { margin-bottom: 0; }

  /* Obs */
  .obs-block { margin-top: 3mm; padding: 4mm 5mm; background: var(--card); border-radius: 2px; }
  .obs-block p { font-size: 9pt; color: var(--ink-2); white-space: pre-wrap; line-height: 1.5; }

  /* Conf strip */
  .conf-strip {
    margin-top: 6mm; padding: 3.5mm 5mm;
    border: 0.75pt solid var(--rule); border-radius: 2px;
    display: flex; align-items: center; gap: 8px;
    font-size: 8.5pt; color: var(--ink-3);
  }
  .conf-strip .lock { width: 4mm; height: 4mm; flex-shrink: 0; color: var(--ink-3); }

  /* Running footer */
  .rf {
    margin-top: auto; padding-top: 5mm; border-top: 0.75pt solid var(--rule);
    display: flex; justify-content: space-between;
    font-family: var(--font-mono); font-size: 7.5pt; color: var(--ink-4); letter-spacing: .02em;
  }

  /* Proteção contra quebra ruim — blocos que não devem cortar no meio. */
  .section-head,
  .client-plate,
  .metric-card,
  .op-grid,
  .deposit-block,
  .pend-callout,
  .obs-block,
  .conf-strip { page-break-inside: avoid; break-inside: avoid; }
  .data-table thead { display: table-header-group; }
  .data-table tr { page-break-inside: avoid; break-inside: avoid; }
  .section-head { page-break-after: avoid; break-after: avoid; }

  @media print {
    html, body { background: white !important; }
    .doc { padding: 0; gap: 0; }
    .page {
      box-shadow: none;
      width: auto;
      min-height: auto;
      padding: 0;
    }
    .page::before { display: none; }
    .no-print { display: none !important; }
    /* Em print, .rh/.rf inline ficam ocultos. Quem aparece em cada página é o
       headerTemplate/footerTemplate injetado pelo Playwright (server-side). */
    .rh, .rf { display: none !important; }
    /* SEM @page margin aqui — quem define as margens é o page.pdf({margin})
       do Playwright. Definir @page { margin: 0 } sobrescreveria as margens
       do DevTools e o header injetado sobreporia o conteúdo. */
  }
`;

function modalidadeLabel(m: string): string {
  if (m === 'custeio') return 'Custeio';
  if (m === 'investimento') return 'Investimento';
  if (m === 'comercializacao') return 'Comercialização';
  return m;
}

function formatTaxa(t: number | null | undefined): string {
  if (t == null) return '—';
  const pct = t < 1 ? t * 100 : t;
  return `${pct.toLocaleString('pt-BR', { maximumFractionDigits: 4 })}% a.a.`;
}

function papelLabel(p: string): string {
  const map: Record<string, string> = {
    emitente_principal: 'Emitente principal',
    emitente_secundario: 'Co-emitente',
    outorga_conjugal: 'Outorga conjugal',
    avalista: 'Avalista',
    fiel_depositario: 'Fiel depositário',
    terceiro_interveniente: 'Interveniente hipotecante',
    terceiro_anuente: 'Terceiro anuente',
  };
  return map[p] ?? p;
}

function garantiaTipoLabel(tipo: string, grau?: number | null): string {
  if (tipo === 'penhor_cedular') return 'Penhor cedular';
  if (tipo === 'penhor_rural') return 'Penhor rural';
  if (tipo === 'alienacao_fiduciaria') return 'Alienação fiduciária';
  if (tipo === 'hipoteca') return grau != null ? `Hipoteca em ${grau}º grau` : 'Hipoteca';
  if (tipo === 'aval') return 'Aval';
  return tipo;
}

function seguroTipoLabel(t: string): string {
  if (t === 'vida_prestamista') return 'Vida prestamista';
  if (t === 'penhor_rural') return 'Penhor rural';
  if (t === 'proagro') return 'Proagro';
  if (t === 'agricola') return 'Agrícola';
  if (t === 'patrimonial') return 'Patrimonial';
  return t;
}

function anexoCategoriaLabel(c: string): string {
  const map: Record<string, string> = {
    cedula: 'Cédula', cet: 'CET', orcamento: 'Orçamento',
    ovpr: 'OVPR / Adesão', aditivo: 'Aditivo', recibo_deposito: 'Recibo de depósito',
    apolice_seguro: 'Apólice de seguro', comprovante_pagamento: 'Comprovante de pagamento',
    outros: 'Outros',
  };
  return map[c] ?? c;
}

function instrumentoTitle(t: string | null | undefined): string {
  if (!t) return 'Operação de Crédito';
  const map: Record<string, string> = {
    'CCB': 'Cédula de Crédito Bancário',
    'CPR': 'Cédula de Produto Rural',
    'CCR': 'Cédula de Crédito Rural',
    'CRPH': 'Cédula Rural Pignoratícia e Hipotecária',
  };
  if (map[t]) return map[t];
  if (/c[eé]dula/i.test(t)) return t;
  return t;
}

function getSafra(dataIni: string | null | undefined): string | null {
  if (!dataIni) return null;
  const d = new Date(dataIni);
  if (isNaN(d.getTime())) return null;
  const m = d.getUTCMonth() + 1;
  const y = d.getUTCFullYear();
  if (m >= 7) return `${y}/${String((y + 1) % 100).padStart(2, '0')}`;
  return `${y - 1}/${String(y % 100).padStart(2, '0')}`;
}

function parcelaBadgeClass(s: string): string {
  if (s === 'paga') return 'badge ok';
  if (s === 'atrasada') return 'badge irr';
  return 'badge pend';
}

function parcelaBadgeLabel(s: string): string {
  if (s === 'paga') return 'Paga';
  if (s === 'atrasada') return 'Atrasada';
  return 'Pendente';
}

function liberacaoBadgeClass(s: string): string {
  if (s === 'liberada') return 'badge ok';
  if (s === 'cancelada') return 'badge irr';
  return 'badge pend';
}

function liberacaoBadgeLabel(s: string): string {
  if (s === 'liberada') return 'Liberada';
  if (s === 'cancelada') return 'Cancelada';
  return 'Prevista';
}

function extrairPendencias(obs: string | null | undefined): string[] {
  if (!obs) return [];
  return obs
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => /^pend[êe]ncia\s*:/i.test(l))
    .map((l) => l.replace(/^pend[êe]ncia\s*:\s*/i, ''));
}

function docSub(op: Operacao): string {
  const partes = [
    op.programa_nome,
    op.cultura,
    op.periodo_agricola_ini ? `Safra ${getSafra(op.periodo_agricola_ini)}` : null,
  ].filter(Boolean);
  return partes.join(' · ');
}

function RelatorioOperacaoRoute() {
  const { opId } = Route.useParams();
  const { data, isLoading } = useOperacaoDetalhe(Number(opId));

  useEffect(() => {
    document.title = data ? `Relatório CCB ${data.numero_cedula}` : 'Relatório de Operação';
  }, [data]);

  if (isLoading) {
    return (
      <>
        <style>{PRINT_CSS}</style>
        <div className="rel-shell" style={{ padding: '2rem', textAlign: 'center', color: '#7a7a7a' }}>
          Carregando relatório…
        </div>
      </>
    );
  }
  if (!data) {
    return (
      <>
        <style>{PRINT_CSS}</style>
        <div className="rel-shell" style={{ padding: '2rem', textAlign: 'center', color: '#7a7a7a' }}>
          Operação não encontrada.
        </div>
      </>
    );
  }

  const pendencias = extrairPendencias(data.observacoes);

  const parcelasPagas = data.parcelas?.filter((p) => p.status === 'paga').length ?? 0;
  const parcelasAtrasadas = data.parcelas?.filter((p) => p.status === 'atrasada').length ?? 0;
  const parcelasPendentes = data.parcelas?.filter((p) => p.status === 'pendente').length ?? 0;
  const totalParcelas = data.parcelas?.length ?? 0;

  const totalLiberado = data.liberacoes
    ?.filter((l) => l.status === 'liberada')
    .reduce((s, l) => s + l.valor, 0) ?? 0;

  const hoje = formatDateBR(new Date().toISOString().slice(0, 10));
  const safra = getSafra(data.periodo_agricola_ini);
  const sub = docSub(data);
  const docTitle = instrumentoTitle(data.tipo_instrumento);
  const footerLabel = `RURALPLAN · RELATÓRIO ${data.tipo_instrumento ?? 'OPERAÇÃO'} ${data.numero_cedula} · GESTÃO DE CRÉDITO RURAL`;

  // Definição de páginas. Cada página é um .page com .rh no topo e .rf no fim.
  // Página 1: header + plate + métricas + Dados + Participantes + Liberações.
  // Página 2: Parcelamento + Garantias + Seguros + Depósito + Orçamento + Anexos + Pendências/Obs + conf-strip.
  // Se algum bloco da página 2 não existir, ele é simplesmente omitido.
  const totalPages = 2;

  return (
    <div className="rel-shell" style={{ fontFamily: 'var(--font-sans)' }}>
      <style>{PRINT_CSS}</style>

      <div className="rel-toolbar no-print">
        <span>Pré-visualização do relatório. Use o botão <strong>PDF</strong> no modal da operação pra baixar o arquivo gerado pelo servidor (sem cabeçalho do navegador).</span>
        <button type="button" className="rel-btn" onClick={() => window.print()}>
          <Printer size={14} strokeWidth={1.5} />
          Imprimir (fallback)
        </button>
      </div>

      <div className="doc">
        {/* ──────────── PÁGINA 1 ──────────── */}
        <div className="page" data-pn="PÁGINA 01">
          <header className="rh">
            <div className="brand"><img src="/logo-ruralplan.svg" alt="Ruralplan" /></div>
            <div className="meta">{hoje}</div>
          </header>

          <div className="doc-label">Relatório · Operação de Crédito</div>
          <h1 className="doc-title">{docTitle}</h1>
          {sub && <div className="doc-sub">{sub}</div>}

          <div className="client-plate">
            <div className="cp-cell">
              <div className="k">Cédula nº</div>
              <div className="v mono">{data.numero_cedula}</div>
            </div>
            <div className="cp-cell">
              <div className="k">Cliente</div>
              <div className="v">{formatName(data.cliente_nome ?? '—')}</div>
            </div>
            <div className="cp-cell">
              <div className="k">CPF</div>
              <div className="v mono">{data.cliente_cpf ? formatCPF(data.cliente_cpf) : '—'}</div>
            </div>
          </div>

          <div className="metric-strip">
            <div className="metric-card accent">
              <div className="m-label">Valor financiado</div>
              <div className="m-value">{formatBRL(data.valor_total)}</div>
              <div className="m-sub">
                recursos próprios: {data.recursos_proprios != null ? formatBRL(data.recursos_proprios) : 'R$ 0,00'}
              </div>
            </div>
            <div className="metric-card">
              <div className="m-label">Taxa c/ bônus</div>
              <div className="m-value">{formatTaxa(data.taxa_juros_aa)}</div>
              <div className="m-sub">
                {[data.programa_nome, data.instituicao_nome].filter(Boolean).join(' · ') || '—'}
              </div>
            </div>
            <div className="metric-card">
              <div className="m-label">Vencimento</div>
              <div className="m-value">{formatDateBR(data.data_vencimento)}</div>
              <div className="m-sub">
                contratação {data.data_contratacao ? formatDateBR(data.data_contratacao) : '—'}
              </div>
            </div>
            <div className="metric-card">
              <div className="m-label">Liquidação</div>
              <div className="m-value">
                {data.tipo_liquidacao === 'unica' ? 'Única' : `${data.num_parcelas} parcelas`}
              </div>
              <div className="m-sub">
                {totalParcelas > 0
                  ? `${parcelasPagas} paga${parcelasPagas === 1 ? '' : 's'} · ${parcelasPendentes + parcelasAtrasadas} pendente${parcelasPendentes + parcelasAtrasadas === 1 ? '' : 's'}`
                  : safra ?? '—'}
              </div>
            </div>
          </div>

          {/* 01 Dados da Operação */}
          <div className="section">
            <div className="section-head">
              <span><span className="s-num">01</span><span className="s-title">Dados da Operação</span></span>
            </div>
            <div className="op-grid">
              <div className="op-cell"><div className="ck">Instrumento</div><div className="cv">{data.tipo_instrumento}</div></div>
              <div className="op-cell">
                <div className="ck">Modalidade</div>
                <div className="cv">
                  {modalidadeLabel(data.modalidade)}
                  {data.setor && ` · ${data.setor === 'pecuario' ? 'Pecuário' : data.setor === 'agricola' ? 'Agrícola' : data.setor}`}
                  {data.subfinalidade && ` · ${data.subfinalidade}`}
                </div>
              </div>
              <div className="op-cell">
                <div className="ck">Instituição</div>
                <div className={`cv${data.instituicao_nome ? '' : ' dash'}`}>{data.instituicao_nome ?? '—'}</div>
              </div>
              <div className="op-cell">
                <div className="ck">Agência</div>
                <div className={`cv${data.agencia_nome ? '' : ' dash'}`}>{data.agencia_nome ?? '—'}</div>
              </div>
              <div className="op-cell">
                <div className="ck">Programa / Linha</div>
                <div className={`cv${data.programa_nome ? '' : ' dash'}`}>{data.programa_nome ?? '—'}</div>
              </div>
              <div className="op-cell">
                <div className="ck">Recurso</div>
                <div className={`cv${data.recurso ? '' : ' dash'}`}>{data.recurso ?? '—'}</div>
              </div>
              <div className="op-cell">
                <div className="ck">Cultura</div>
                <div className={`cv${data.cultura ? '' : ' dash'}`}>{data.cultura ?? '—'}</div>
              </div>
              <div className="op-cell">
                <div className="ck">Área financiada</div>
                <div className={`cv${data.area_ha != null ? '' : ' dash'}`}>
                  {data.area_ha != null ? `${data.area_ha.toLocaleString('pt-BR')} ha` : '—'}
                </div>
              </div>
              <div className="op-cell">
                <div className="ck">Contratação</div>
                <div className={`cv${data.data_contratacao ? '' : ' dash'}`}>
                  {data.data_contratacao ? formatDateBR(data.data_contratacao) : '—'}
                </div>
              </div>
              <div className="op-cell">
                <div className="ck">Vencimento</div>
                <div className="cv">{formatDateBR(data.data_vencimento)}</div>
              </div>
              <div className="op-cell">
                <div className="ck">Taxa s/ bônus</div>
                <div className={`cv${data.taxa_juros_aa_sem_bonus != null ? '' : ' dash'}`}>
                  {data.taxa_juros_aa_sem_bonus != null ? formatTaxa(data.taxa_juros_aa_sem_bonus) : '—'}
                </div>
              </div>
              <div className="op-cell">
                <div className="ck">Carência</div>
                <div className={`cv${data.carencia_meses != null ? '' : ' dash'}`}>
                  {data.carencia_meses != null ? `${data.carencia_meses} meses` : '—'}
                </div>
              </div>
            </div>
          </div>

          {/* 02 Participantes */}
          {data.participantes && data.participantes.length > 0 && (
            <div className="section">
              <div className="section-head">
                <span><span className="s-num">02</span><span className="s-title">Participantes</span></span>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Papel</th>
                    <th>Nome</th>
                    <th>CPF / CNPJ</th>
                  </tr>
                </thead>
                <tbody>
                  {data.participantes.map((p) => (
                    <tr key={p.id}>
                      <td>{papelLabel(p.papel)}</td>
                      <td className={p.papel === 'emitente_principal' ? 'strong' : ''}>
                        {formatName(p.nome ?? p.cliente_nome_link ?? '—')}
                      </td>
                      <td className={p.cpf ? 'mono' : 'dash'}>{p.cpf ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 03 Liberações */}
          {data.liberacoes && data.liberacoes.length > 0 && (
            <div className="section">
              <div className="section-head">
                <span><span className="s-num">03</span><span className="s-title">Liberações</span></span>
                <span className="s-meta">total liberado: {formatBRL(totalLiberado)}</span>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '8mm' }}>#</th>
                    <th>Data prevista</th>
                    <th>Data efetivada</th>
                    <th className="r">Valor</th>
                    <th className="r">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.liberacoes.map((lib, i) => (
                    <tr key={lib.id}>
                      <td className="mono">{i + 1}</td>
                      <td>
                        {lib.data_prevista
                          ? formatDateBR(lib.data_prevista)
                          : lib.referencia_textual === 'IMEDIATA'
                            ? `Imediata${lib.previsao_operacional_dias ? ` (~${lib.previsao_operacional_dias} dias)` : ''}`
                            : '—'}
                      </td>
                      <td className={lib.data_efetivada ? '' : 'dash'}>
                        {lib.data_efetivada ? formatDateBR(lib.data_efetivada) : '—'}
                      </td>
                      <td className="r mono strong">{formatBRL(lib.valor)}</td>
                      <td className="r"><span className={liberacaoBadgeClass(lib.status)}>{liberacaoBadgeLabel(lib.status)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <footer className="rf">
            <div>{footerLabel}</div>
            <div>PÁGINA 1 / {totalPages}</div>
          </footer>
        </div>

        {/* ──────────── PÁGINA 2 ──────────── */}
        <div className="page" data-pn="PÁGINA 02">
          <header className="rh">
            <div className="brand"><img src="/logo-ruralplan.svg" alt="Ruralplan" /></div>
            <div className="meta">{hoje}</div>
          </header>

          {/* 04 Parcelamento */}
          {data.parcelas && data.parcelas.length > 0 && (
            <div className="section" style={{ marginTop: '4mm' }}>
              <div className="section-head">
                <span><span className="s-num">04</span><span className="s-title">Parcelamento</span></span>
                <span className="s-meta">
                  {totalParcelas} parcelas · liquidação {data.tipo_liquidacao === 'unica' ? 'única' : 'parcelada'}
                </span>
              </div>
              <div className="progress-row">
                <div className="progress-segs">
                  {data.parcelas.map((p) => (
                    <div key={p.id} className={`seg ${p.status === 'paga' ? 'paid' : p.status === 'atrasada' ? 'late' : 'pend'}`} />
                  ))}
                </div>
                <div className="progress-label">
                  <b>{parcelasPagas}</b> / {totalParcelas} parcelas pagas
                </div>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '8mm' }}>#</th>
                    <th>Vencimento</th>
                    <th className="r">Valor previsto</th>
                    <th className="r">Valor pago</th>
                    <th>Pagamento</th>
                    <th className="r">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.parcelas.map((p) => (
                    <tr key={p.id}>
                      <td className="mono">{p.numero}</td>
                      <td>{formatDateBR(p.data_vencimento)}</td>
                      <td className="r mono">
                        {p.valor_previsto != null
                          ? formatBRL(p.valor_previsto)
                          : p.percentual_saldo_devedor != null
                            ? `${p.percentual_saldo_devedor.toLocaleString('pt-BR')}% do saldo`
                            : '—'}
                      </td>
                      <td className={p.valor_pago != null ? 'r mono strong' : 'r dash'}>
                        {p.valor_pago != null ? formatBRL(p.valor_pago) : '—'}
                      </td>
                      <td className={p.data_pagamento ? '' : 'dash'}>
                        {p.data_pagamento ? formatDateBR(p.data_pagamento) : '—'}
                      </td>
                      <td className="r"><span className={parcelaBadgeClass(p.status)}>{parcelaBadgeLabel(p.status)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 05 Garantias */}
          {data.garantias && data.garantias.length > 0 && (
            <div className="section">
              <div className="section-head">
                <span><span className="s-num">05</span><span className="s-title">Garantias</span></span>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Cultura / Imóvel</th>
                    <th>Matrícula</th>
                    <th className="r">Quantidade</th>
                    <th className="r">Valor unitário</th>
                    <th className="r">Valor total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.garantias.map((g) => {
                    const isPenhor = g.tipo === 'penhor_cedular' || g.tipo === 'penhor_rural';
                    const isAnimal = isPenhor && g.quantidade_cabecas != null;
                    const valorTotal = g.valor_estimado ?? g.valor_avaliacao;
                    const qtde = isAnimal
                      ? `${g.quantidade_cabecas!.toLocaleString('pt-BR')} cab`
                      : g.quantidade_sacas != null ? `${g.quantidade_sacas.toLocaleString('pt-BR')} sc`
                      : g.quantidade_kg != null ? `${g.quantidade_kg.toLocaleString('pt-BR')} kg`
                      : null;
                    const vUnit = isAnimal && g.valor_unitario != null ? `${formatBRL(g.valor_unitario)}/cab`
                      : g.valor_saca != null ? formatBRL(g.valor_saca)
                      : null;
                    const culturaImovel = isPenhor && g.produto
                      ? `${g.produto}${g.area_plantada_ha != null && !isAnimal ? ` — ${g.area_plantada_ha.toLocaleString('pt-BR')} ha plantados` : ''}`
                      : g.imovel_denominacao ?? g.descricao ?? '—';
                    const subline = isAnimal && g.categoria_animal
                      ? `${g.raca_animal ? g.raca_animal + ' · ' : ''}${g.categoria_animal}${g.idade_min_meses != null ? ` · ${g.idade_min_meses}${g.idade_max_meses ? '-' + g.idade_max_meses : '+'} meses` : ''}`
                      : null;
                    return (
                      <tr key={g.id}>
                        <td>{garantiaTipoLabel(g.tipo, g.grau_hipoteca)}</td>
                        <td className={g.imovel_denominacao ? 'strong' : ''}>
                          {culturaImovel}
                          {subline && <span className="sub">{subline}</span>}
                        </td>
                        <td className={g.imovel_matricula ? 'mono' : 'dash'}>{g.imovel_matricula ?? '—'}</td>
                        <td className={qtde ? 'r mono' : 'r dash'}>{qtde ?? '—'}</td>
                        <td className={vUnit ? 'r mono' : 'r dash'}>{vUnit ?? '—'}</td>
                        <td className={valorTotal != null ? 'r mono strong' : 'r dash'}>
                          {valorTotal != null ? formatBRL(valorTotal) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* 06 Seguros */}
          {data.seguros && data.seguros.length > 0 && (
            <div className="section">
              <div className="section-head">
                <span><span className="s-num">06</span><span className="s-title">Seguros Contratados</span></span>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Seguradora / Produto</th>
                    <th className="r">Capital segurado</th>
                    <th className="r">Prêmio</th>
                    <th className="r">Vigência</th>
                  </tr>
                </thead>
                <tbody>
                  {data.seguros.map((s) => (
                    <tr key={s.id}>
                      <td>{seguroTipoLabel(s.tipo)}</td>
                      <td>{s.produto ?? s.seguradora ?? '—'}</td>
                      <td className={s.capital_segurado != null ? 'r mono' : 'r dash'}>
                        {s.capital_segurado != null ? formatBRL(s.capital_segurado) : '—'}
                      </td>
                      <td className="r mono">{formatBRL(s.premio)}</td>
                      <td className={s.vigencia_fim ? 'r' : 'r dash'}>
                        {s.vigencia_fim ? formatDateBR(s.vigencia_fim) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 07 Depósito de Grãos */}
          {data.depositos && data.depositos.length > 0 && (
            <div className="section">
              <div className="section-head">
                <span><span className="s-num">07</span><span className="s-title">Depósito de Grãos em Armazém</span></span>
              </div>
              {data.depositos.map((d) => (
                <div key={d.id} className="deposit-block">
                  <div className="db-cell">
                    <div className="dk">Produto</div>
                    <div className="dv">
                      {d.produto}
                      {d.quantidade_sacas != null && ` · ${d.quantidade_sacas.toLocaleString('pt-BR')} sc`}
                    </div>
                    {d.valor_saca != null && <div className="dv small">{formatBRL(d.valor_saca)} / sc</div>}
                  </div>
                  <div className="db-cell">
                    <div className="dk">Armazenador</div>
                    <div className="dv">{d.armazem_nome ?? '—'}</div>
                    {d.armazem_endereco && (
                      <div className="dv small">
                        {d.armazem_endereco}
                        {d.armazem_municipio && ` · ${d.armazem_municipio}/${d.armazem_uf}`}
                      </div>
                    )}
                  </div>
                  {d.fiel_depositario && (
                    <div className="db-cell">
                      <div className="dk">Fiel depositário</div>
                      <div className="dv">{d.fiel_depositario}</div>
                    </div>
                  )}
                  {d.data_deposito && (
                    <div className="db-cell">
                      <div className="dk">Data do depósito</div>
                      <div className="dv">{formatDateBR(d.data_deposito)}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 08 Orçamento de Aplicação */}
          {data.orcamento_itens && data.orcamento_itens.length > 0 && (
            <div className="section">
              <div className="section-head">
                <span><span className="s-num">08</span><span className="s-title">Orçamento de Aplicação</span></span>
                <span className="s-meta">
                  total: {formatBRL(data.orcamento_itens.reduce((s, i) => s + (i.valor_total ?? 0), 0))}
                </span>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Grupo</th>
                    <th>Discriminação</th>
                    <th>Und</th>
                    <th className="r">Qtde</th>
                    <th className="r">Vr. unit.</th>
                    <th className="r">Rec. próprios</th>
                    <th className="r">Financiado</th>
                    <th className="r">Total</th>
                    <th>Época</th>
                  </tr>
                </thead>
                <tbody>
                  {data.orcamento_itens.map((it) => (
                    <tr key={it.id}>
                      <td style={{ fontSize: '7.5pt', color: 'var(--ink-4)' }}>{it.grupo ?? '—'}</td>
                      <td>{it.discriminacao}</td>
                      <td>{it.unidade ?? '—'}</td>
                      <td className={it.quantidade != null ? 'r mono' : 'r dash'}>
                        {it.quantidade != null ? it.quantidade.toLocaleString('pt-BR', { maximumFractionDigits: 4 }) : '—'}
                      </td>
                      <td className={it.valor_unitario != null ? 'r mono' : 'r dash'}>
                        {it.valor_unitario != null ? formatBRL(it.valor_unitario) : '—'}
                      </td>
                      <td className={it.recursos_proprios != null && it.recursos_proprios > 0 ? 'r mono' : 'r dash'}>
                        {it.recursos_proprios != null && it.recursos_proprios > 0 ? formatBRL(it.recursos_proprios) : '—'}
                      </td>
                      <td className={it.valor_financiado != null ? 'r mono' : 'r dash'}>
                        {it.valor_financiado != null ? formatBRL(it.valor_financiado) : '—'}
                      </td>
                      <td className={it.valor_total != null ? 'r mono strong' : 'r dash'}>
                        {it.valor_total != null ? formatBRL(it.valor_total) : '—'}
                      </td>
                      <td style={{ fontSize: '7.5pt', color: 'var(--ink-4)' }}>{it.epoca ?? '—'}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '0.75pt solid var(--rule)', fontWeight: 600 }}>
                    <td colSpan={5} className="r strong" style={{ paddingTop: '2.5mm' }}>TOTAL</td>
                    <td className="r mono strong" style={{ paddingTop: '2.5mm' }}>
                      {formatBRL(data.orcamento_itens.reduce((s, i) => s + (i.recursos_proprios ?? 0), 0))}
                    </td>
                    <td className="r mono strong" style={{ paddingTop: '2.5mm' }}>
                      {formatBRL(data.orcamento_itens.reduce((s, i) => s + (i.valor_financiado ?? 0), 0))}
                    </td>
                    <td className="r mono strong" style={{ paddingTop: '2.5mm' }}>
                      {formatBRL(data.orcamento_itens.reduce((s, i) => s + (i.valor_total ?? 0), 0))}
                    </td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* 09 Anexos */}
          {data.anexos && data.anexos.length > 0 && (
            <div className="section">
              <div className="section-head">
                <span><span className="s-num">09</span><span className="s-title">Anexos</span></span>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Categoria</th>
                    <th>Documento</th>
                    <th className="r">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {data.anexos.map((a) => (
                    <tr key={a.id}>
                      <td>{anexoCategoriaLabel(a.categoria)}</td>
                      <td>{a.titulo}</td>
                      <td className={a.data_documento ? 'r mono' : 'r dash'}>
                        {a.data_documento ? formatDateBR(a.data_documento) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pendências */}
          {pendencias.length > 0 && (
            <div className="section">
              <div className="section-head">
                <span><span className="s-num">10</span><span className="s-title">Pendências</span></span>
                <span className="s-meta">{pendencias.length} item{pendencias.length === 1 ? '' : 's'}</span>
              </div>
              <div className="pend-callout">
                <ul>
                  {pendencias.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            </div>
          )}

          <div className="conf-strip">
            <svg className="lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>
              Todas as informações são tratadas em caráter <strong>estritamente confidencial</strong>, em conformidade com a Lei Geral de Proteção de Dados (13.709/2018).
            </span>
          </div>

          <footer className="rf">
            <div>{footerLabel}</div>
            <div>PÁGINA 2 / {totalPages}</div>
          </footer>
        </div>
      </div>
    </div>
  );
}
